import { useState } from "react"
import { FormField, Select } from "@smooth/design-system-components"
import { useDunningSystemPromptVersions } from "../../hooks/useDunningSystemPromptVersions"
import type { DunningSystemPromptVersion } from "../../types"
import type { GeneratePrompt } from "../types"

/** 有効版のほかに選択肢・一覧へそのまま出す、新しい版の数 */
export const RECENT_VERSION_COUNT = 3

/** 選択肢に添えるメモの長さ。超えたら省略する */
export const NOTE_LABEL_LENGTH = 24

/** 版のメモの短い表示（長ければ省略。無ければ空） */
export const shortNote = (note: string | null | undefined) => {
  const text = (note ?? "").trim()
  if (!text) return ""
  const chars = [...text]
  return chars.length > NOTE_LABEL_LENGTH ? `${chars.slice(0, NOTE_LABEL_LENGTH).join("")}…` : text
}

/** 版の名前（例「v19（使用中） · 一部入金の言い回し調整」）。メモがあれば添える */
export const versionLabel = (item: DunningSystemPromptVersion) => {
  const note = shortNote(item.note)
  return `v${item.version}${item.active ? "（使用中）" : ""}${note ? ` · ${note}` : ""}`
}

/**
 * 版を「有効版 → 新しい順」に並べ、先頭に出すもの（有効版と新しい版 RECENT_VERSION_COUNT 本）とそれ以外に分ける
 */
export const splitVersions = (versions: DunningSystemPromptVersion[]) => {
  const byNewest = [...versions].sort((a, b) => b.version - a.version)
  const active = byNewest.filter((item) => item.active)
  const others = byNewest.filter((item) => !item.active)
  return {
    shown: [...active, ...others.slice(0, RECENT_VERSION_COUNT)],
    rest: others.slice(RECENT_VERSION_COUNT),
  }
}

/**
 * 生成結果に出すプロンプトの版名（例：v18）。保存済みの版一覧から id を引き、
 * 一覧に無い（読み込み中・取得失敗など）ときは「版 #id」と出す。本文を直接渡した生成なら「編集したプロンプト」
 */
export function usePromptVersionLabel() {
  const { versions } = useDunningSystemPromptVersions()
  return (promptVersionId: number | null | undefined) => {
    if (!promptVersionId) return "編集したプロンプト"
    const found = versions.find((item) => item.id === promptVersionId)
    return found ? `v${found.version}` : `版 #${promptVersionId}`
  }
}

/**
 * 生成に使うプロンプトの選択。保存済みの版から選ぶ（既定は有効版。モデルは版に付いたものを使う）。
 * 選択肢は有効版と新しい版を先に並べ、古い版もその後ろに残す（古い版でも流し直せるように）
 */
export function usePromptChoice() {
  const [versionId, setVersionId] = useState("")
  const { versions, isLoading, error } = useDunningSystemPromptVersions()
  const { shown, rest } = splitVersions(versions)
  const ordered = [...shown, ...rest]
  const version = versions.find((item) => item.id === Number(versionId)) ?? ordered[0]
  const prompt: GeneratePrompt | null = version ? { promptVersionId: version.id } : null

  return { versions: ordered, isLoading, error, version, select: setVersionId, prompt }
}

export type PromptChoice = ReturnType<typeof usePromptChoice>

/** 「使うプロンプト」の選択欄 */
export function PromptFields({ choice }: { choice: PromptChoice }) {
  const { versions, isLoading, error, version } = choice
  const noVersionLabel = isLoading
    ? "読み込み中…"
    : error
      ? "取得に失敗しました"
      : "保存済みプロンプトなし"

  return (
    <FormField label="使うプロンプト">
      <Select
        value={version ? String(version.id) : ""}
        disabled={isLoading}
        onChange={(e) => choice.select(e.target.value)}
        options={
          versions.length
            ? versions.map((item) => ({ value: String(item.id), label: versionLabel(item) }))
            : [{ value: "", label: noVersionLabel }]
        }
      />
    </FormField>
  )
}
